import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Beaker, FlaskConical, ArrowRightLeft, AlertTriangle, ChevronDown, ChevronUp, ShieldCheck } from 'lucide-react';
import type { AssessmentResult, EquipmentDosingReq } from '@/types/calculator';
import { AVAILABLE_CHEMICALS } from '@/types/calculator';

interface ProcessTrainDosingProps {
  result: AssessmentResult;
  onApplyAlternative?: (eqId: string, chemicalId: string, dose: number) => void;
}

export default function ProcessTrainDosing({ result, onApplyAlternative }: ProcessTrainDosingProps) {
  const reqs = result.equipmentDosingRequirements ?? [];
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selectedAlt, setSelectedAlt] = useState<Record<string, string>>({});

  if (reqs.length === 0) {
    return (
      <div className="rb-card p-6 text-center">
        <ShieldCheck className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
        <p className="text-sm text-slate-400">No additional dosing required for the current process train.</p>
        <p className="text-xs text-slate-600 mt-1">All equipment can operate with current chemical configuration.</p>
      </div>
    );
  }

  const severityStyle = (s: string) => {
    if (s === 'required') return 'bg-red-500/10 border-red-500/20 text-red-400';
    if (s === 'recommended') return 'bg-rb-brand/10 border-rb-brand/20 text-rb-brand';
    return 'bg-cyan-500/10 border-cyan-500/20 text-cyan-400';
  };

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
          <FlaskConical className="w-5 h-5 text-cyan-400" />
        </div>
        <div>
          <h2 className="text-sm font-semibold text-white">Equipment-Specific Dosing Recommendations</h2>
          <p className="text-xs text-slate-500">
            {reqs.length} equipment{reqs.length > 1 ? 's' : ''} require chemical adjustment
          </p>
        </div>
      </div>

      {/* Equipment Cards */}
      <div className="space-y-3">
        {reqs.map((req) => {
          const isOpen = expanded === req.equipmentId;
          const selectedChemId = selectedAlt[req.equipmentId] ?? req.recommendedChemicals[0]?.chemicalId;
          const selectedChem = req.recommendedChemicals.find(c => c.chemicalId === selectedChemId) || req.recommendedChemicals[0];
          const chemInfo = AVAILABLE_CHEMICALS.find(c => c.id === selectedChemId);

          return (
            <div
              key={req.equipmentId}
              className="rb-card overflow-hidden"
            >
              {/* Card Header */}
              <button
                onClick={() => setExpanded(isOpen ? null : req.equipmentId)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-800/20 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center">
                    <Beaker className="w-4 h-4 text-rb-brand" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white">{req.equipmentName}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-md border font-bold uppercase tracking-wider ${severityStyle(req.severity)}`}>
                        {req.severity}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{req.reason}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {req.requiresCoagulant && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 border border-red-500/20">Coagulant</span>
                  )}
                  {req.requiresFlocculant && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">Flocculant</span>
                  )}
                  {req.requiresPhAdjustment && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">pH Adj</span>
                  )}
                  {isOpen ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                </div>
              </button>

              {/* Expanded Content */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-4 pt-0 border-t border-slate-800/50 space-y-4">
                      {/* Reason */}
                      <div className="flex items-start gap-2 mt-3">
                        <AlertTriangle className="w-4 h-4 text-rb-brand mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-slate-400 leading-relaxed">{req.reason}</p>
                      </div>

                      {/* Chemical Alternatives */}
                      <div className="space-y-2">
                        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                          <ArrowRightLeft className="w-3.5 h-3.5" />
                          Choose Chemical Alternative
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                          {req.recommendedChemicals.map((chem) => {
                            const isSelected = selectedChemId === chem.chemicalId;
                            const chemMeta = AVAILABLE_CHEMICALS.find(c => c.id === chem.chemicalId);
                            return (
                              <button
                                key={chem.chemicalId}
                                onClick={() => setSelectedAlt(prev => ({ ...prev, [req.equipmentId]: chem.chemicalId }))}
                                className={`relative p-3 rounded-xl border text-left transition-all ${
                                  isSelected
                                    ? 'bg-rb-brand/10 border-rb-brand/40'
                                    : 'bg-slate-800/30 border-slate-700 hover:border-slate-600'
                                }`}
                              >
                                {isSelected && (
                                  <div className="absolute top-2 right-2 w-2 h-2 rounded-full bg-rb-brand" />
                                )}
                                <div className="text-sm font-bold text-white">{chem.name}</div>
                                <div className="text-[10px] text-slate-500 font-mono mt-0.5">{chem.formula}</div>
                                <div className="text-[10px] text-slate-400 mt-2 leading-relaxed">{chem.reason}</div>
                                <div className="mt-2 flex items-center gap-1.5">
                                  <span className="text-xs font-bold text-cyan-400">{chem.recommendedDose} {chem.doseUnit}</span>
                                  {chemMeta && (
                                    <span className="text-[10px] text-slate-600">
                                      pH {chemMeta.phChangePerMgL > 0 ? '+' : ''}{(chemMeta.phChangePerMgL * chem.recommendedDose).toFixed(1)}
                                    </span>
                                  )}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Apply Button */}
                      {onApplyAlternative && selectedChem && (
                        <motion.button
                          whileTap={{ scale: 0.97 }}
                          onClick={() => onApplyAlternative(req.equipmentId, selectedChem.chemicalId, selectedChem.recommendedDose)}
                          className="w-full py-2.5 rounded-lg text-sm font-medium bg-gradient-to-r from-rb-brand to-[#b8931f] text-slate-200 hover:from-[#b8931f] hover:to-[#a07d1a] transition-all shadow-lg shadow-[#cda42e]/10"
                        >
                          Apply {selectedChem.name} at {selectedChem.recommendedDose} {selectedChem.doseUnit} to {req.equipmentName}
                        </motion.button>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
